import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { DEFAULT_MENU_CONFIG, needsConfigUpdate } from '@/config/defaultMenuConfig';

interface CustomGroup {
  id: string;
  title: string;
  modules: string[];
  order: number;
  isCollapsed?: boolean;
}

interface ModuleOrderPreference {
  id: string;
  user_id: string;
  company_id: string;
  module_orders: Record<string, string[]>; // category -> module IDs in order
  custom_groups: CustomGroup[]; // grupos personalizados con títulos
  created_at: string;
  updated_at: string;
}

export function useModuleOrder() {
  const { user, selectedCompany } = useAuth();
  const { toast } = useToast();
  const [moduleOrders, setModuleOrders] = useState<Record<string, string[]>>({});
  const [customGroups, setCustomGroups] = useState<CustomGroup[]>([]);
  const [loading, setLoading] = useState(false);

  console.log('🏗️ [useModuleOrder] Hook initialized');
  console.log('🏗️ [useModuleOrder] Current customGroups state:', customGroups);

  const loadUserModuleOrder = useCallback(async () => {
    console.log('🔄 [useModuleOrder] loadUserModuleOrder called');
    console.log('👤 [useModuleOrder] User ID:', user?.id);
    console.log('🏢 [useModuleOrder] Company ID:', selectedCompany?.id);
    
    if (!user?.id || !selectedCompany?.id) {
      console.log('❌ [useModuleOrder] Missing user or company ID, skipping load');
      return;
    }

    setLoading(true);
    console.log('📡 [useModuleOrder] Loading user module preferences...');
    
    try {
      // Primero obtener el corporate group de la empresa actual
      const { data: companyData } = await supabase
        .from('companies')
        .select(`
          id,
          corporate_group_id,
          corporate_groups(
            id,
            menu_config
          )
        `)
        .eq('id', selectedCompany.id)
        .maybeSingle();

      console.log('🏢 [useModuleOrder] Company data:', companyData);
      console.log('🏢 [useModuleOrder] Corporate group config:', companyData?.corporate_groups?.menu_config);

      const { data, error } = await supabase
        .from('user_module_preferences')
        .select('module_orders, custom_groups')
        .eq('user_id', user.id)
        .eq('company_id', selectedCompany.id)
        .maybeSingle();

      if (error) {
        console.warn('⚠️ [useModuleOrder] Error loading preferences:', error.message);
      }

      if (data && data.custom_groups && Array.isArray(data.custom_groups)) {
        // Usar preferencias de usuario si existen
        console.log('📊 [useModuleOrder] User preferences loaded:', data);
        setModuleOrders((data.module_orders as Record<string, string[]>) || {});
        setCustomGroups((data.custom_groups as unknown) as CustomGroup[]);
      } else {
        // Usar configuración del corporate group como fallback
        console.log('⚠️ [useModuleOrder] No user preferences, using corporate group config');
        const corporateMenuConfig = companyData?.corporate_groups?.menu_config as any;
        console.log('🏢 [useModuleOrder] Corporate menu config:', corporateMenuConfig);
        
        if (corporateMenuConfig && typeof corporateMenuConfig === 'object') {
          // Verificar si la configuración necesita actualización a formato homologado
          if (needsConfigUpdate(corporateMenuConfig)) {
            console.log('🔄 [useModuleOrder] Corporate config needs update to homologated format');
            
            // Usar configuración predeterminada homologada
            const defaultConfig = DEFAULT_MENU_CONFIG;
            setCustomGroups(defaultConfig.customGroups);
            setModuleOrders(defaultConfig.moduleOrder);
            
            // Opcional: guardar la configuración actualizada en el corporate group
            try {
              await supabase
                .from('corporate_groups')
                .update({ 
                  menu_config: defaultConfig as any,
                  updated_at: new Date().toISOString()
                })
                .eq('id', companyData?.corporate_group_id);
              console.log('✅ [useModuleOrder] Corporate config updated to homologated format');
            } catch (updateError) {
              console.warn('⚠️ [useModuleOrder] Could not update corporate config:', updateError);
            }
          } else {
            // Configuración ya está en formato homologado
            console.log('✅ [useModuleOrder] Corporate config is already homologated');
            
            if (corporateMenuConfig.customGroups && Array.isArray(corporateMenuConfig.customGroups)) {
              const corporateCustomGroups = corporateMenuConfig.customGroups.map((group: any) => ({
                id: group.id,
                title: group.title,
                modules: Array.isArray(group.modules) ? group.modules : [],
                order: group.order || 0,
                isCollapsed: group.isCollapsed || false
              })) as CustomGroup[];
              setCustomGroups(corporateCustomGroups);
            } else {
              setCustomGroups([]);
            }
            
            if (corporateMenuConfig.moduleOrder) {
              setModuleOrders(corporateMenuConfig.moduleOrder as Record<string, string[]>);
            } else {
              setModuleOrders({});
            }
          }
        } else {
          console.log('❌ [useModuleOrder] No corporate menu config found, using default homologated config');
          setCustomGroups(DEFAULT_MENU_CONFIG.customGroups);
          setModuleOrders(DEFAULT_MENU_CONFIG.moduleOrder);
        }
      }
    } catch (error) {
      console.error('❌ [useModuleOrder] Error loading module order:', error);
      setCustomGroups([]);
      setModuleOrders({});
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedCompany?.id]);

  const saveModuleOrder = useCallback(async (category: string, moduleIds: string[]) => {
    if (!user?.id || !selectedCompany?.id) return;

    const updatedOrders = {
      ...moduleOrders,
      [category]: moduleIds
    };

    try {
      const { error } = await supabase
        .from('user_module_preferences')
        .upsert({
          user_id: user.id,
          company_id: selectedCompany.id,
          module_orders: updatedOrders as any,
          custom_groups: customGroups as any,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,company_id'
        });

      if (error) throw error;

      setModuleOrders(updatedOrders);
      
      toast({
        title: "Orden guardado",
        description: "La organización de módulos se ha guardado correctamente.",
      });
    } catch (error) {
      console.error('Error saving module order:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el orden de los módulos.",
        variant: "destructive",
      });
    }
  }, [user?.id, selectedCompany?.id, moduleOrders, customGroups, toast]);

  const saveCustomGroups = useCallback(async (groups: CustomGroup[]) => {
    if (!user?.id || !selectedCompany?.id) return;

    try {
      const { error } = await supabase
        .from('user_module_preferences')
        .upsert({
          user_id: user.id,
          company_id: selectedCompany.id,
          module_orders: moduleOrders as any,
          custom_groups: groups as any,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,company_id'
        });

      if (error) throw error;

      setCustomGroups(groups);
      
      toast({
        title: "Grupos guardados",
        description: "Los grupos personalizados se han guardado correctamente.",
      });
    } catch (error) {
      console.error('Error saving custom groups:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar los grupos personalizados.",
        variant: "destructive",
      });
    }
  }, [user?.id, selectedCompany?.id, moduleOrders, toast]);

  const createCustomGroup = useCallback((title: string) => {
    const newGroup: CustomGroup = {
      id: crypto.randomUUID(),
      title,
      modules: [],
      order: customGroups.length,
      isCollapsed: false
    };
    
    const updatedGroups = [...customGroups, newGroup];
    saveCustomGroups(updatedGroups);
    return newGroup.id;
  }, [customGroups, saveCustomGroups]);

  const updateCustomGroup = useCallback((groupId: string, updates: Partial<CustomGroup>) => {
    const updatedGroups = customGroups.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    );
    saveCustomGroups(updatedGroups);
  }, [customGroups, saveCustomGroups]);

  const deleteCustomGroup = useCallback((groupId: string) => {
    const updatedGroups = customGroups.filter(group => group.id !== groupId);
    saveCustomGroups(updatedGroups);
  }, [customGroups, saveCustomGroups]);

  const moveModuleToGroup = useCallback((moduleId: string, sourceGroupId: string | null, targetGroupId: string | null) => {
    const updatedGroups = customGroups.map(group => {
      if (group.id === sourceGroupId) {
        return { ...group, modules: group.modules.filter(id => id !== moduleId) };
      }
      if (group.id === targetGroupId) {
        return { ...group, modules: [...group.modules, moduleId] };
      }
      return group;
    });
    saveCustomGroups(updatedGroups);
  }, [customGroups, saveCustomGroups]);

  const reorderModulesInGroup = useCallback((groupId: string, startIndex: number, endIndex: number) => {
    const updatedGroups = customGroups.map(group => {
      if (group.id === groupId) {
        const reorderedModules = [...group.modules];
        const [removed] = reorderedModules.splice(startIndex, 1);
        reorderedModules.splice(endIndex, 0, removed);
        return { ...group, modules: reorderedModules };
      }
      return group;
    });
    saveCustomGroups(updatedGroups);
  }, [customGroups, saveCustomGroups]);

  const reorderCustomGroups = useCallback((startIndex: number, endIndex: number) => {
    const reorderedGroups = [...customGroups];
    const [removed] = reorderedGroups.splice(startIndex, 1);
    reorderedGroups.splice(endIndex, 0, removed);
    
    // Actualizar el orden de los grupos
    const updatedGroups = reorderedGroups.map((group, index) => ({
      ...group,
      order: index
    }));
    
    saveCustomGroups(updatedGroups);
  }, [customGroups, saveCustomGroups]);

  const getOrderedModules = useCallback((category: string, modules: any[]) => {
    const savedOrder = moduleOrders[category];
    if (!savedOrder || !Array.isArray(savedOrder) || savedOrder.length === 0) {
      return modules;
    }

    // Ordenar según las preferencias guardadas
    const ordered: any[] = [];
    const unordered: any[] = [];

    // Primero agregar módulos en el orden guardado
    savedOrder.forEach(moduleId => {
      const module = modules.find(m => m.id === moduleId);
      if (module) {
        ordered.push(module);
      }
    });

    // Luego agregar módulos nuevos que no están en el orden guardado
    modules.forEach(module => {
      if (!savedOrder.includes(module.id)) {
        unordered.push(module);
      }
    });

    return [...ordered, ...unordered];
  }, [moduleOrders]);

  useEffect(() => {
    console.log('🔄 [useModuleOrder] useEffect triggered');
    console.log('👤 [useModuleOrder] User ID in effect:', user?.id);
    console.log('🏢 [useModuleOrder] Company ID in effect:', selectedCompany?.id);
    console.log('🔄 [useModuleOrder] About to call loadUserModuleOrder...');
    loadUserModuleOrder();
  }, [loadUserModuleOrder]);

  return {
    moduleOrders,
    customGroups,
    loading,
    saveModuleOrder,
    saveCustomGroups,
    createCustomGroup,
    updateCustomGroup,
    deleteCustomGroup,
    moveModuleToGroup,
    reorderModulesInGroup,
    reorderCustomGroups,
    getOrderedModules,
    refetch: loadUserModuleOrder
  };
}